import React, { forwardRef, useImperativeHandle } from 'react';
import DatePicker from '@/components/form/DatePicker';
import ModalDatePicker from '@/components/form/ModalDatePicker';
import TimeDropdown from './TimeDropdown';
import Checkbox from '../controls/Checkbox';
import { createLogger } from '@/utils/logger/devLogger';

export interface DateTimeFieldsProps {
  values: {
    startDate: Date;
    endDate: Date;
    isAllDay: boolean;
  };
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errors: {
    startDate?: string;
    endDate?: string;
  };
  className?: string;
  onValidation?: (isValid: boolean, errors: { startDate?: string; endDate?: string }) => void;
  // TailAdmin backdrop support
  useModal?: boolean;
  backdrop?: boolean | 'blur' | 'solid' | 'none';
  backdropOpacity?: 'light' | 'medium' | 'dark';
}

// Ref interface for modal integration
export interface DateTimeFieldSetRef {
  validate: () => boolean;
  getValues: () => { startDate: Date; endDate: Date; isAllDay: boolean };
  getErrors: () => { startDate?: string; endDate?: string };
}

// Create logger for this component
const dateTimeLogger = createLogger('DateTimeFieldSet');

const DateTimeFieldSet = forwardRef<DateTimeFieldSetRef, DateTimeFieldsProps>(({ 
  values, 
  onChange, 
  errors,
  className = '',
  onValidation,
  // TailAdmin backdrop props
  useModal = false,
  backdrop = 'blur',
  backdropOpacity = 'medium'
}, ref) => {
  // Create a synthetic change event
  const createChangeEvent = (name: string, value: unknown) => {
    const event = {
      target: {
        name,
        value
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    onChange(event);
  };

  // Handle all day toggle
  const handleAllDayToggle = (checked: boolean) => {
    
    createChangeEvent('isAllDay', checked);
    
    // When switching to all day event, adjust the times
    if (checked) {
      const startDate = new Date(values.startDate);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(values.endDate);
      endDate.setHours(23, 59, 59, 999);
      
      createChangeEvent('startDate', startDate);
      createChangeEvent('endDate', endDate);
    } else {
      // When switching back, preserve the existing times if they're reasonable
      // Only set default times if the current times are all-day times (00:00 or 23:59)
      const startDate = new Date(values.startDate);
      const endDate = new Date(values.endDate);
      
      // Check if currently set to all-day times
      const isCurrentlyAllDayStart = startDate.getHours() === 0 && startDate.getMinutes() === 0;
      const isCurrentlyAllDayEnd = endDate.getHours() === 23 && endDate.getMinutes() === 59;
      
      if (isCurrentlyAllDayStart || isCurrentlyAllDayEnd) {
        // Only set default times if coming from all-day times
        const now = new Date();
        startDate.setHours(now.getHours(), 0, 0, 0);
        endDate.setHours(now.getHours() + 1, 0, 0, 0);
        
        console.log('🔧 ALL-DAY OFF: Setting default times (was all-day)', {
          newStartTime: `${startDate.getHours()}:${String(startDate.getMinutes()).padStart(2, '0')}`,
          newEndTime: `${endDate.getHours()}:${String(endDate.getMinutes()).padStart(2, '0')}`
        });
        
        createChangeEvent('startDate', startDate);
        createChangeEvent('endDate', endDate);
      } else {
        // Preserve existing times - user probably has specific times selected
        // No need to call createChangeEvent - just preserve current values
      }
    }
  };

  // Handle start date change
  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      // Create new Date objects to avoid mutating the original (timezone-safe)
      const startDate = new Date(date);
      
      
      // If all day event, set time to start of day
      if (values.isAllDay) {
        startDate.setHours(0, 0, 0, 0);
      }
      
      // Always update end date to be 1 hour after start date for timed events
      const updatedEndDate = new Date(startDate);
      if (values.isAllDay) {
        updatedEndDate.setHours(23, 59, 59, 999);
      } else {
        // Set end time to start time + 1 hour
        updatedEndDate.setHours(startDate.getHours() + 1, startDate.getMinutes(), 0, 0);
      }
      
      
      // Update both start and end dates
      createChangeEvent('startDate', startDate);
      createChangeEvent('endDate', updatedEndDate);
    }
  };

  // Handle end date change
  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      // Create new Date object to avoid mutating the original (timezone-safe)
      const endDate = new Date(date);
      
      // If all day event, set time to end of day
      if (values.isAllDay) {
        endDate.setHours(23, 59, 59, 999);
      }
      
      // Check if the end date is before the start date
      const startDate = new Date(values.startDate);
      if (endDate < startDate) {
        // Create a new start date that's 1 hour before the end date
        const updatedStartDate = new Date(endDate);
        if (values.isAllDay) {
          updatedStartDate.setHours(0, 0, 0, 0);
        } else {
          // Subtract 1 hour from end time for start time
          updatedStartDate.setHours(endDate.getHours() - 1, endDate.getMinutes(), 0, 0);
        }
        
        createChangeEvent('startDate', updatedStartDate);
      }
      
      createChangeEvent('endDate', endDate);
    }
  };

  // Validate date/time fields
  const validateDateTime = (): boolean => {
    const validationErrors: { startDate?: string; endDate?: string } = {};
    let isValid = true;
    
    // Validate start date
    if (!values.startDate) {
      validationErrors.startDate = 'Start date is required';
      isValid = false;
    }
    
    // Validate end date
    if (!values.endDate) {
      validationErrors.endDate = 'End date is required';
      isValid = false;
    } else if (values.startDate && values.endDate < values.startDate) {
      validationErrors.endDate = 'End date/time must be after start date/time';
      isValid = false;
    }
    
    dateTimeLogger.info('DateTime validation:', isValid ? 'passed' : 'failed', { 
      errors: validationErrors, 
      values 
    });
    
    // Notify parent component of validation result
    if (onValidation) {
      onValidation(isValid, validationErrors);
    }
    
    return isValid;
  };

  // Expose validation methods to parent components via ref
  useImperativeHandle(ref, () => ({
    validate: validateDateTime,
    getValues: () => values,
    getErrors: () => errors
  }), [values, errors]);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* All Day Event checkbox */}
      <Checkbox
        id="isAllDay"
        name="isAllDay"
        checked={values.isAllDay}
        onChange={handleAllDayToggle}
        label="All Day Event"
        className="items-center"
      />
      
      {values.isAllDay ? (
        /* All Day Event Layout - Combined Date/Time pickers */
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* Start Date */}
          <div className="relative">
            {useModal ? (
              <ModalDatePicker
                label="Start Date"
                id="startDate"
                name="startDate"
                value={values.startDate}
                onChange={handleStartDateChange}
                showTimeSelect={false}
                error={errors.startDate}
                required
                backdrop={backdrop}
                backdropOpacity={backdropOpacity}
              />
            ) : (
              <DatePicker
                label="Start Date"
                id="startDate"
                defaultDate={values.startDate}
                onChange={(selectedDates) => {
                  if (selectedDates && selectedDates.length > 0) {
                    handleStartDateChange(selectedDates[0]);
                  }
                }}
                placeholder="Select start date"
              />
            )}
          </div>
          
          {/* End Date */}
          <div className="relative">
            {useModal ? (
              <ModalDatePicker
                label="End Date"
                id="endDate"
                name="endDate"
                value={values.endDate}
                onChange={handleEndDateChange}
                showTimeSelect={false}
                error={errors.endDate}
                required
                backdrop={backdrop}
                backdropOpacity={backdropOpacity}
              />
            ) : (
              <DatePicker
                label="End Date"
                id="endDate"
                defaultDate={values.endDate}
                onChange={(selectedDates) => {
                  if (selectedDates && selectedDates.length > 0) {
                    handleEndDateChange(selectedDates[0]);
                  }
                }}
                placeholder="Select end date"
              />
            )}
          </div>
        </div>
      ) : (
        /* Timed Event Layout - Date and Times on single row */
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {/* Date Field */}
          <div className="relative">
            {useModal ? (
              <ModalDatePicker
                label="Date"
                id="eventDate"
                name="eventDate"
                value={values.startDate}
                onChange={(date) => {
                  if (date) {
                    // Update both start and end date to the same date, preserving times
                    const startTime = values.startDate.getHours();
                    const startMinutes = values.startDate.getMinutes();
                    const endTime = values.endDate.getHours();
                    const endMinutes = values.endDate.getMinutes();
                    
                    const newStartDate = new Date(date);
                    newStartDate.setHours(startTime, startMinutes, 0, 0);
                    
                    const newEndDate = new Date(date);
                    newEndDate.setHours(endTime, endMinutes, 0, 0);
                    
                    handleStartDateChange(newStartDate);
                    handleEndDateChange(newEndDate);
                  }
                }}
                showTimeSelect={false}
                error={errors.startDate}
                required
                backdrop={backdrop}
                backdropOpacity={backdropOpacity}
              />
            ) : (
              <DatePicker
                label="Date"
                id="eventDate"
                defaultDate={values.startDate}
                onChange={(selectedDates) => {
                  if (selectedDates && selectedDates.length > 0) {
                    const date = selectedDates[0];
                    // Update both start and end date to the same date, preserving times
                    const startTime = values.startDate.getHours();
                    const startMinutes = values.startDate.getMinutes();
                    const endTime = values.endDate.getHours();
                    const endMinutes = values.endDate.getMinutes();
                    
                    const newStartDate = new Date(date);
                    newStartDate.setHours(startTime, startMinutes, 0, 0);
                    
                    const newEndDate = new Date(date);
                    newEndDate.setHours(endTime, endMinutes, 0, 0);
                    
                    handleStartDateChange(newStartDate);
                    handleEndDateChange(newEndDate);
                  }
                }}
                placeholder="Select date"
              />
            )}
          </div>
          
          {/* Start Time */}
          <div className="relative">
            <TimeDropdown
              id="startTime"
              name="startTime"
              label="Start Time"
              value={values.startDate}
              onChange={handleStartDateChange}
              error={errors.startDate}
              required
            />
          </div>
          
          {/* End Time */}
          <div className="relative">
            <TimeDropdown
              id="endTime"
              name="endTime"
              label="End Time"
              value={values.endDate}
              onChange={handleEndDateChange}
              error={errors.endDate}
              required
              startTime={values.startDate}
              showDuration={true}
            />
          </div>
        </div>
      )}
    </div>
  );
});

DateTimeFieldSet.displayName = 'DateTimeFieldSet';

export default DateTimeFieldSet; 